import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { ChevronMark, Wordmark } from "../components/ChevronMark";

const inputClass =
  "h-12 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-gold focus:ring-2 focus:ring-gold/25";

export function AuthBrandPanel() {
  return (
    <div className="relative hidden flex-col justify-between overflow-hidden bg-forest-deep p-12 lg:flex">
      <div className="bg-noise pointer-events-none absolute inset-0" />
      <Link to="/" className="relative flex items-center gap-3" data-testid="auth-logo">
        <ChevronMark light className="h-9 w-auto" />
        <Wordmark light />
      </Link>
      <div className="relative">
        <p className="font-editorial text-3xl italic leading-snug text-bone">
          “The cost of the wrong path is always higher than the cost of the right guidance.”
        </p>
        <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.35em] text-gold">
          Path · Pace · Way
        </p>
      </div>
      <p className="relative text-xs text-emerald-100/40">
        Personalized education &amp; career consulting for students, parents, and professionals.
      </p>
    </div>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-bone pt-[72px] lg:grid-cols-2" data-testid="login-page">
      <AuthBrandPanel />
      <div className="flex items-center justify-center px-4 py-16 sm:px-8">
        <div className="w-full max-w-md">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold-dark">Welcome back</p>
          <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-forest-deep">Log in</h1>
          <p className="mt-2 text-sm text-muted-foreground">Access your LearnYourWay account.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <input data-testid="login-email-input" type="email" required autoComplete="email"
              className={inputClass} placeholder="Email address"
              value={email} onChange={(e) => setEmail(e.target.value)} />
            <input data-testid="login-password-input" type="password" required autoComplete="current-password"
              className={inputClass} placeholder="Password"
              value={password} onChange={(e) => setPassword(e.target.value)} />
            {error && (
              <p data-testid="login-error" className="rounded-xl bg-destructive/10 px-4 py-2.5 text-sm font-medium text-destructive">
                {error}
              </p>
            )}
            <button type="submit" data-testid="login-submit-btn" disabled={loading}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-forest font-display text-sm font-bold text-bone transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(19,78,58,0.35)] disabled:opacity-60">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Log in
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between text-sm">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-muted-foreground transition-colors hover:text-forest">
              Forgot password?
            </Link>
            <Link to="/register" data-testid="go-to-register-link" className="font-semibold text-forest transition-colors hover:text-gold-dark">
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
